import React from "react";
import {
  Grid,
  makeStyles,
  Typography,
  Paper,
  Button,
  FormControlLabel,
  Checkbox,
} from "@material-ui/core";
import { ValidatorForm, TextValidator } from 'react-material-ui-form-validator';
import useError from "Hooks/useError";
import Alert from "@material-ui/lab/Alert";
import MuiAlert from "@material-ui/lab/Alert";
import { message } from "constants/message";

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2),
    display: "flex",
    overflow: "auto",
    flexDirection: "column",
  },
  wrapper: {
    marginTop: theme.spacing(4),
  },
  messsageWrapper: {
    marginTop: theme.spacing(1),
  },
  alert: {
    marginTop: theme.spacing(2),
  },
  label: {
    '&.Mui-disabled': {
      color: 'rgba(0, 0, 0, 0.87)',
    },
  },
}));

export default function Form({ submit, accountAttr, dict = null, isEdit = false }) {
  const classes = useStyles();
  const stateError = useError();
  const messages = message();
  const [name, setName] = React.useState("");
  const [desc, setDesc] = React.useState("");
  const [label, setLabel] = React.useState("");
  const [words, setWords] = React.useState([]);
  const [filteredWords, setFilteredWords] = React.useState([]);
  const [ignore_case, setIgnore_case] = React.useState(false);
  const [isRunning, setIsRunning] = React.useState(false);
  const [maxLabelWords, setMaxLabelWords] = React.useState(0);
  const [count, setCount] = React.useState(0);

  // 辞書を参照しているタスクに「実行中」「実行待ち」ステータスのものがあったら true を返す
  const isReferrersStatusRunning = (dict) => {
    const refIDs = dict.data.referenced;
    if (!refIDs || refIDs.length < 1) return false;

    // 「実行中」「実行待ち」ステータスのタスクを集めたオブジェクト生成
    let runningReferrers = {};
    const referrers = dict.meta.referrers;
    for(let i=0; i<referrers.length; ++i) {
      const referrer = referrers[i];
      if (referrer.status=="running" || referrer.status=="waiting") {
        const tid = referrer.id;
        runningReferrers[tid] = 1;
      }
    }

    for(let i=0; i<refIDs.length; ++i) {
      if (runningReferrers.hasOwnProperty(refIDs[i])) {
        return true;
      }
    }
    return false;
  }

  React.useEffect(() => {
    stateError.initialize();
  }, []);

  React.useEffect(() => {
    if (!dict) return;
    setName(dict.data.name);
    setDesc(dict.data.desc);
    setLabel(dict.data.label);
    setWords(dict.data.words);
    setIgnore_case(dict.data.ignore_case);
    setIsRunning(isReferrersStatusRunning(dict));
  }, [dict]);

  React.useEffect(() => {
    if (!accountAttr) return;
    setMaxLabelWords(accountAttr.max_label_words);
  }, [accountAttr]);

  React.useEffect(() => {
    let cnt = 0;
    setFilteredWords([]);

    if (words?.length) {
      // 空行と重複を削除した配列にする
      const splitRow = [...new Set(words.filter((v) => v))];
      setFilteredWords(splitRow);
      cnt = splitRow.length;
      splitRow.map((row) => {
        cnt += row.replace(/,$/g, "").split(",").filter((v) => v).length - 1;
      });
    }

    setCount(cnt);
  }, [words]);

  // キーワード入力上限数超過確認用バリデーションを追加
  ValidatorForm.addValidationRule('checkKeywordsCount', (value) => {
    if (value && maxLabelWords) {
      if (count > maxLabelWords) {
        return false;
      }
    }
    return true;
  });

  const onError = () => {
    stateError.dispatch({
      type: "set",
      payload: {
        isError: true,
        errorMsg: messages.validation.common,
      },
    });
  };

  return (
    <Grid>
      <ValidatorForm
        onSubmit={(e) => {
          e.preventDefault();
          submit({ name, desc, label, filteredWords, ignore_case });
        }}
        onError={onError}
      >
        <Paper elevation={3} className={classes.paper}>
          <Typography variant="h6" color="primary">
            {isEdit ? "分類辞書更新" : "分類辞書登録"}
          </Typography>
          {isRunning && (
            <Alert severity="error" className={classes.alert}>
              「実行待ち」または「実行中」ステータスのタスクに設定されている辞書は編集できません。
            </Alert>
          )}
          <Grid item xs={12} className={classes.wrapper}>
            <TextValidator
              label="辞書名*"
              fullWidth
              value={name}
              inputProps={{ maxLength: 32767, readOnly: isRunning }}
              onChange={(event) => {
                setName(event.target.value);
              }}
              validators={['required']}
              errorMessages={[messages.validation.required]}
            />
          </Grid>
          <Grid item xs={12} className={classes.wrapper}>
            <TextValidator
              label="概要"
              fullWidth
              value={desc}
              inputProps={{ maxLength: 32767, readOnly: isRunning }}
              onChange={(event) => {
                setDesc(event.target.value);
              }}
            />
          </Grid>
          <Grid item xs={12} className={classes.wrapper}>
            <TextValidator
              label="ラベル*"
              fullWidth
              value={label}
              inputProps={{ maxLength: 32767, readOnly: isRunning }}
              onChange={(event) => {
                setLabel(event.target.value);
              }}
              validators={['required']}
              errorMessages={[messages.validation.required]}
            />
          </Grid>
          <Grid item xs={12} className={classes.wrapper}>
            <TextValidator
              label="キーワード*"
              multiline
              minRows={8}
              maxRows={8}
              fullWidth
              value={words.join("\n")}
              inputProps={{ readOnly: isRunning }}
              onChange={(event) => {
                setWords(event.target.value.split("\n"));
              }}
              validators={['required', 'checkKeywordsCount']}
              errorMessages={[messages.validation.required, messages.validation.keywordsCount.replace("$1", count).replace("$2", "分類辞書毎登録キーワード上限数").replace("$3", maxLabelWords)]}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="p">
              ※複数キーワードを改行区切りで指定します
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              classes={{ label: classes.label, }}
              control={
                <Checkbox
                  onChange={(event) => {
                    setIgnore_case(event.target.checked);
                  }}
                  color="primary"
                  checked={ignore_case}
                  disabled={isRunning}
                />
              }
              label="大文字/小文字を区別しない"
            />
          </Grid>
        </Paper>

        {stateError.state.isError && (
          <div className={classes.messsageWrapper}>
            <MuiAlert
              elevation={6}
              variant="filled"
              severity="error"
              className={classes.toast}
            >
              {stateError.state.errorMsg}
            </MuiAlert>
          </div>
        )}
        <Grid container justifyContent="center" className={classes.wrapper}>
          <Button type="submit" variant="contained" color="primary" disabled={isRunning}>
            {isEdit ? "更新" : "登録"}
          </Button>
        </Grid>
      </ValidatorForm>
    </Grid>
  );
}
